// ================================================================
// PromptsPanel — مرکز پرامپت صنفی (نسخه‌ی backend / async)
// ================================================================

import './prompts.css';
import { t } from '../../i18n/index.js';
import { icon, escapeHtml, delegate } from '../../utils/dom.js';
import {
  TRADES, getVersions, getTradesWithVersions, saveVersion,
  setActive, moveToTrash, restoreVersion, purgeVersion, getAllTrash
} from '../../core/prompts-store.js';

let overlay = null;
const s = {
  view: 'editor',
  selectedTrade: null,
  draft: '',
  viewingId: null,
  confirmDelete: null,
  confirmPurge: null,
  loading: false,
  versions: [],
  tradesWithVersions: [],
  trashItems: []
};

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch (e) {
    return iso;
  }
}

function tradeLabel(key) {
  const tr = TRADES.find(x => x.key === key);
  return tr ? t(tr.labelKey) : key;
}

export function openPromptsPanel() {
  if (overlay) return;
  overlay = document.createElement('div');
  overlay.className = 'prompts-overlay';
  document.body.appendChild(overlay);
  Object.assign(s, {
    view: 'editor', selectedTrade: null, draft: '', viewingId: null,
    confirmDelete: null, confirmPurge: null, loading: false,
    versions: [], tradesWithVersions: [], trashItems: []
  });
  draw();
  delegate(overlay, '[data-action]', 'click', onAction);
  delegate(overlay, 'textarea[data-draft]', 'input', (e, el) => { s.draft = el.value; });
  loadTrades();
}

function close() {
  if (overlay) { overlay.remove(); overlay = null; }
}

async function loadTrades() {
  try {
    s.tradesWithVersions = await getTradesWithVersions();
    draw();
  } catch (e) { console.error('loadTrades failed', e); }
}

async function loadVersions(key) {
  s.loading = true; draw();
  try {
    s.versions = await getVersions(key);
  } catch (e) {
    console.error('loadVersions failed', e);
    s.versions = [];
  }
  s.loading = false; draw();
}

async function loadTrash() {
  s.loading = true; draw();
  try {
    s.trashItems = await getAllTrash();
  } catch (e) {
    console.error('loadTrash failed', e);
    s.trashItems = [];
  }
  s.loading = false; draw();
}

async function selectTrade(key) {
  s.view = 'editor';
  s.selectedTrade = key;
  s.viewingId = null;
  s.confirmDelete = null;
  s.draft = '';
  await loadVersions(key);
  // پیش‌فرض: متن فعال یا جدیدترین در ویرایشگر
  const active = s.versions.find(v => v.isActive);
  const latest = s.versions[0];
  s.draft = active ? active.text : (latest ? latest.text : '');
  draw();
}

function onAction(e, el) {
  const a = el.dataset.action;
  const id = el.dataset.id;
  switch (a) {
    case 'close': return close();
    case 'select-trade': return selectTrade(el.dataset.key);
    case 'open-trash': {
      s.view = 'trash';
      s.confirmPurge = null;
      loadTrash();
      return;
    }
    case 'save': return doSave();
    case 'view-version': {
      const v = s.versions.find(x => x.id === id);
      if (v) { s.draft = v.text; s.viewingId = v.id; draw(); }
      return;
    }
    case 'back-to-edit': {
      const active = s.versions.find(v => v.isActive);
      const latest = s.versions[0];
      s.draft = active ? active.text : (latest ? latest.text : '');
      s.viewingId = null;
      draw();
      return;
    }
    case 'set-active': return doSetActive(id);
    case 'ask-delete': { s.confirmDelete = id; return draw(); }
    case 'cancel-delete': { s.confirmDelete = null; return draw(); }
    case 'confirm-delete': return doDelete(id);
    case 'restore': return doRestore(id);
    case 'ask-purge': { s.confirmPurge = id; return draw(); }
    case 'cancel-purge': { s.confirmPurge = null; return draw(); }
    case 'confirm-purge': return doPurge(id);
  }
}

async function doSave() {
  if (!s.selectedTrade || !s.draft.trim()) return;
  try {
    await saveVersion(s.selectedTrade, s.draft);
    s.viewingId = null;
    await loadVersions(s.selectedTrade);
    await loadTrades();
  } catch (e) { console.error('save failed', e); }
}

async function doSetActive(id) {
  try {
    await setActive(id);
    await loadVersions(s.selectedTrade);
  } catch (e) { console.error('activate failed', e); }
}

async function doDelete(id) {
  try {
    await moveToTrash(id);
    if (s.viewingId === id) {
      s.viewingId = null;
      const latest = s.versions.find(v => v.id !== id);
      s.draft = latest ? latest.text : '';
    }
    s.confirmDelete = null;
    await loadVersions(s.selectedTrade);
    await loadTrades();
  } catch (e) { console.error('delete failed', e); }
}

async function doRestore(id) {
  try {
    await restoreVersion(id);
    await loadTrash();
    await loadTrades();
  } catch (e) { console.error('restore failed', e); }
}

async function doPurge(id) {
  try {
    await purgeVersion(id);
    s.confirmPurge = null;
    await loadTrash();
  } catch (e) { console.error('purge failed', e); }
}

function drawSidebar() {
  const tradesList = TRADES.map(tr => {
    const active = (s.view === 'editor' && tr.key === s.selectedTrade) ? 'active' : '';
    const has = s.tradesWithVersions.includes(tr.key) ? '<span class="pr-dot"></span>' : '';
    return `
      <button class="pr-trade ${active}" data-action="select-trade" data-key="${tr.key}">
        <span>${escapeHtml(tradeLabel(tr.key))}</span>
        ${has}
      </button>
    `;
  }).join('');

  const trashActive = s.view === 'trash' ? 'active' : '';
  const trashCount = s.trashItems.length;
  const countBadge = trashCount > 0 ? `<span class="pr-trash-count">${trashCount}</span>` : '';

  return `
    <aside class="pr-sidebar">
      <div class="pr-sidebar-title">${escapeHtml(t('prompts.trades'))}</div>
      ${tradesList}
      <button class="pr-trade pr-trash-nav ${trashActive}" data-action="open-trash">
        ${icon('delete', 18)}
        <span>${escapeHtml(t('prompts.trash'))}</span>
        ${countBadge}
      </button>
    </aside>
  `;
}

function drawEditor() {
  if (!s.selectedTrade) {
    return `<div class="pr-empty">${escapeHtml(t('prompts.selectTrade'))}</div>`;
  }
  const viewing = s.viewingId !== null;

  const historyList = s.versions.length
    ? s.versions.map((v, idx) => {
        const isLatest = idx === 0;
        const sel = v.id === s.viewingId ? 'active' : '';
        const confirming = s.confirmDelete === v.id;
        const active = v.isActive;
        return `
          <div class="pr-version-row ${sel} ${active ? 'pr-row-active' : ''}">
            <button class="pr-version" data-action="view-version" data-id="${escapeHtml(v.id)}">
              ${icon('history', 14)}
              <span>${escapeHtml(fmtDate(v.createdAt))}</span>
              ${isLatest ? `<span class="pr-latest-badge">${escapeHtml(t('prompts.latest'))}</span>` : ''}
              ${active ? `<span class="pr-active-badge">${escapeHtml(t('prompts.active'))}</span>` : ''}
            </button>
            ${active ? '' : `<button class="pr-set-active" data-action="set-active" data-id="${escapeHtml(v.id)}" title="${escapeHtml(t('prompts.setActive'))}">${icon('radio_button_unchecked', 16)}</button>`}
            ${confirming
              ? `<span class="pr-confirm">
                   <button class="pr-confirm-yes" data-action="confirm-delete" data-id="${escapeHtml(v.id)}" title="${escapeHtml(t('prompts.confirmDelete'))}">${icon('check', 16)}</button>
                   <button class="pr-confirm-no" data-action="cancel-delete" title="${escapeHtml(t('prompts.cancel'))}">${icon('close', 16)}</button>
                 </span>`
              : (active
                  ? `<button class="pr-version-del pr-version-del-disabled" disabled title="${escapeHtml(t('prompts.cannotDeleteActive'))}">${icon('delete', 16)}</button>`
                  : `<button class="pr-version-del" data-action="ask-delete" data-id="${escapeHtml(v.id)}" title="${escapeHtml(t('prompts.delete'))}">${icon('delete', 16)}</button>`)
            }
          </div>
        `;
      }).join('')
    : `<div class="pr-no-history">${escapeHtml(t('prompts.noHistory'))}</div>`;

  return `
    <div class="pr-editor-head">
      <h3>${escapeHtml(tradeLabel(s.selectedTrade))}</h3>
      ${viewing
        ? `<button class="pr-btn-secondary" data-action="back-to-edit">${icon('edit', 16)} ${escapeHtml(t('prompts.backToEdit'))}</button>`
        : `<button class="pr-btn-primary" data-action="save">${icon('save', 16)} ${escapeHtml(t('prompts.save'))}</button>`
      }
    </div>
    ${viewing ? `<div class="pr-viewing-note">${icon('visibility', 14)} ${escapeHtml(t('prompts.viewingOld'))}</div>` : ''}
    <textarea class="pr-textarea" data-draft ${viewing ? 'readonly' : ''} placeholder="${escapeHtml(t('prompts.placeholder'))}">${escapeHtml(s.draft)}</textarea>
    <div class="pr-history">
      <div class="pr-history-title">${icon('history', 16)} ${escapeHtml(t('prompts.history'))}</div>
      ${historyList}
    </div>
  `;
}

function drawTrash() {
  if (!s.trashItems.length) {
    return `
      <div class="pr-editor-head"><h3>${escapeHtml(t('prompts.trash'))}</h3></div>
      <div class="pr-empty">${escapeHtml(t('prompts.trashEmpty'))}</div>
    `;
  }
  const rows = s.trashItems.map(it => {
    const purging = s.confirmPurge === it.id;
    return `
      <div class="pr-trash-card">
        <div class="pr-trash-info">
          <div class="pr-trash-trade">${escapeHtml(tradeLabel(it.trade))}</div>
          <div class="pr-trash-meta">
            <span>${icon('history', 13)} ${escapeHtml(fmtDate(it.createdAt))}</span>
            <span class="pr-trash-deleted">${icon('delete', 13)} ${escapeHtml(fmtDate(it.deletedAt))}</span>
          </div>
        </div>
        <div class="pr-trash-actions">
          <button class="pr-version-del pr-restore" data-action="restore" data-id="${escapeHtml(it.id)}" title="${escapeHtml(t('prompts.restore'))}">${icon('restore_from_trash', 16)}</button>
          ${purging
            ? `<span class="pr-confirm">
                 <button class="pr-confirm-yes" data-action="confirm-purge" data-id="${escapeHtml(it.id)}" title="${escapeHtml(t('prompts.confirmDelete'))}">${icon('check', 16)}</button>
                 <button class="pr-confirm-no" data-action="cancel-purge" title="${escapeHtml(t('prompts.cancel'))}">${icon('close', 16)}</button>
               </span>`
            : `<button class="pr-version-del" data-action="ask-purge" data-id="${escapeHtml(it.id)}" title="${escapeHtml(t('prompts.purge'))}">${icon('delete_forever', 16)}</button>`
          }
        </div>
      </div>
    `;
  }).join('');
  return `
    <div class="pr-editor-head"><h3>${icon('delete', 18)} ${escapeHtml(t('prompts.trash'))}</h3></div>
    <div class="pr-trash-list">${rows}</div>
  `;
}

function draw() {
  if (!overlay) return;
  const main = s.view === 'trash' ? drawTrash() : drawEditor();
  overlay.innerHTML = `
    <div class="pr-panel">
      <div class="pr-header">
        <h2>${icon('auto_awesome', 20)} ${escapeHtml(t('prompts.title'))}</h2>
        <button class="pr-close" data-action="close">${icon('close', 20)}</button>
      </div>
      <div class="pr-body">
        ${drawSidebar()}
        <section class="pr-main">
          ${main}
        </section>
      </div>
    </div>
  `;
}
